# Route: /profile
# User profile editor
# Permission: the page, the account read-out, Logout and the tour replay are open to every
# signed-in user (the sidebar row is `permission: "public"`). Saving the profile calls
# PUT /api/users/me/profile, which requires `User:update_self`, so the Save/Cancel row is gated
# on that grant and a user without it is never offered a Save the API answers 403.

page-user-profile:
  type: Page
  archetype: edit
  meta:
    title: $t.profile.meta_title
    route: /profile
    layout: shell
    auth: required
  data:
    profile:
      model: UserProfile
      refresh: on_mount
    form:
      model: UpdateProfileForm
      # Seed the form from the already-fetched profile so users see their current
      # values instead of a blank form. The widgets stage their values in this
      # same draft so Save is the page's only persistence boundary.
      initial:
        displayName: $data.profile.displayName
        language: $data.profile.language
        timezone: $data.profile.timezone
        theme: $data.profile.theme
        avatarOperation: keep
        avatarData: ""
        avatarContentType: ""
        todayWidgetChangesJson: ""
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - profile-header
    - profile-form

# ── Header ───────────────────────────────────────────────────────────────────
#
# F71 §C2 — Logout lives on the profile page so users cannot misclick it from
# the sidebar. It renders as a header action on the "My Profile" title row
# (far right, no scrolling) and dispatches the `logout` custom event the
# router handles end-to-end (clears OIDC session, navigates to
# end_session_endpoint, lands on the post-logout page).

profile-header:
  type: PageHeader
  title: $t.profile.title
  actions:
    - label: $t.profile.btn_logout
      node_id: profile-logout-button
      type: custom
      event: logout
      color: stop
      variant: outlined

# ── Account (F79 it.3 #6) ────────────────────────────────────────────────────
#
# The signed-in account first, then transactional identity and preferences:
# avatar, display name, language, and timezone.

profile-account-group:
  type: FieldGroup
  legend: $t.profile.account_section_title
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
  children:
    - profile-signed-in-as
    - profile-field-photo
    - profile-field-name
    - profile-field-language
    - profile-field-timezone

# The account the session is signed in with, read from the router's user
# context (the id_token `email` claim). Read-only: the identity provider owns it.
profile-signed-in-as:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.2xs
  children:
    - profile-signed-in-as-label
    - profile-signed-in-as-value

profile-signed-in-as-label:
  type: Text
  content: $t.profile.signed_in_as
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

profile-signed-in-as-value:
  type: Text
  content: $user.email
  tokens:
    font_weight: "600"
    color: $color_scheme.light.color.on-surface

profile-form:
  type: Form
  model: UpdateProfileForm
  a11y:
    label: $t.profile.title
  action:
    type: update
    model: UserProfile
    on_success: navigate(/today)
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
  children:
    - profile-account-group
    - profile-appearance-group
    - profile-today-view-widget
    - profile-onboarding-group
    - profile-field-theme
    - profile-field-avatar-operation
    - profile-field-avatar-data
    - profile-field-avatar-content-type
    - profile-field-today-widget-changes
    - profile-save-permission-note
    - profile-form-actions
  # F71 §2 — Default Area field removed from M1 portal shell. Area entity
  # was retired in favour of DomainNode (Organization aggregate); the
  # UserProfile.DefaultAreaId column + DTO + command param remain as
  # orphan refs awaiting full rename. Tracked as BACKLOG Feature 76
  # (Default Domain rename + Combobox UI bound to /api/me/available-domains).

# The avatar control validates and previews locally, then stages the operation,
# plain base64 data, and content type in the canonical profile draft. It performs
# no network write of its own.
profile-field-photo:
  type: Widget
  ref: xp87-avatar-upload
  params:
    photo-url: $data.profile.photoUrl
    photo-version: $data.profile.photoVersion
    display-name: $data.profile.displayName

profile-field-name:
  type: Input
  input_type: text
  label: $t.profile.display_name
  bind: form.displayName
  required: true
  a11y:
    label: $t.profile.display_name_label

profile-field-language:
  type: Input
  input_type: select
  label: $t.profile.language
  bind: form.language
  a11y:
    label: $t.profile.language_label

profile-field-timezone:
  type: Input
  input_type: select
  label: $t.profile.timezone
  bind: form.timezone
  a11y:
    label: $t.profile.timezone_label

# The widgets update these hidden fields through the page's canonical
# xp85-filter-changed draft contract. Their names match UpdateProfileRequest.
profile-field-theme:
  type: Input
  input_type: hidden
  bind: form.theme

profile-field-avatar-operation:
  type: Input
  input_type: hidden
  bind: form.avatarOperation

profile-field-avatar-data:
  type: Input
  input_type: hidden
  bind: form.avatarData

profile-field-avatar-content-type:
  type: Input
  input_type: hidden
  bind: form.avatarContentType

profile-field-today-widget-changes:
  type: Input
  input_type: hidden
  bind: form.todayWidgetChangesJson

# Static note: CIR has no negated permission guard (`permission:` / `permission_any:` /
# `permission_all:` only admit a grant, and `visible_when:` reads page data, not the caller's
# permissions), so the note cannot be shown only to a user who lacks the grant. It says what
# Save needs, which is true for every reader, and explains the missing Save row to the one
# who lacks it.
profile-save-permission-note:
  type: Text
  content: $t.profile.save_permission_note
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

# Gated on the grant PUT /api/users/me/profile requires. Without it a user would fill the form,
# press Save and be routed to /errors/access-denied with the draft lost.
profile-form-actions:
  type: FormActions
  permission: "User:update_self"
  submit_label: $t.common.btn_save
  cancel_route: /today
  cancel_history: false

# ── Appearance (F79 it.3 #6) ─────────────────────────────────────────────────
#
# Color-scheme picker. The legend names the section; the button row is the
# radiogroup. Each button updates form.theme and previews the scheme until the
# profile update saves or navigation cancels it.

profile-appearance-group:
  type: FieldGroup
  legend: $t.profile.appearance_section_title
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
  children:
    - profile-theme-buttons

profile-theme-buttons:
  type: Section
  a11y:
    role: radiogroup
    label: $t.profile.theme_label
  spatial:
    display: flex
    direction: row
    gap: $spacing.sm
  children:
    - profile-theme-light
    - profile-theme-dark
    - profile-theme-system

profile-theme-light:
  type: Button
  variant: outlined
  content: $t.profile.theme_light_icon_label
  a11y:
    role: radio
    label: $t.profile.theme.light
  action:
    type: custom
    event: theme-select-light
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.md

profile-theme-dark:
  type: Button
  variant: outlined
  content: $t.profile.theme_dark_icon_label
  a11y:
    role: radio
    label: $t.profile.theme.dark
  action:
    type: custom
    event: theme-select-dark
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.md

profile-theme-system:
  type: Button
  variant: outlined
  content: $t.profile.theme_system_icon_label
  a11y:
    role: radio
    label: $t.profile.theme.system
  action:
    type: custom
    event: theme-select-system
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.md

# ── Onboarding section (Feature #1315) ───────────────────────────────────────
# Replay entry point for the first-run guided tour. It lives on Profile rather
# than the sidebar: this page is already the home for per-user presentation
# choices (theme, /today widgets) and for the actions F71 §C2 moved off the
# nav, and the sidebar's collapsed icon rail has no affordance for a
# label-bearing action row. Replay re-runs the sequence WITHOUT clearing the
# dismissal flag, so it never resurrects the tour on the next login.
profile-onboarding-group:
  type: FieldGroup
  legend: $t.profile.onboarding_section_title
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
  children:
    - profile-onboarding-replay

profile-onboarding-replay:
  type: Button
  variant: outlined
  content: $t.profile.onboarding_replay
  a11y:
    label: $t.profile.onboarding_replay
  action:
    type: custom
    event: xp87-replay-onboarding-tour
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.md

# ── Today view section (§A.16) ───────────────────────────────────────────────
# One Widget node (<today-widget-settings>): it fetches the permission-resolved
# widget catalog (GET /api/today/widgets?record=false). Toggles render locally
# and stage a JSON delta list in form.todayWidgetChangesJson for profile Save.
#
# F79 it.3 (#6) — the component renders its OWN fieldset + legend, so it sits as
# a direct page child (no outer Section wrapper); that single fieldset is the
# section border, matching the other titled Sections (same tokens). This removes the
# former double border ("2 visual delimiters").
profile-today-view-widget:
  type: Widget
  ref: today-widget-settings
  permission: "public"
